import {
  existsSync,
  lstatSync,
  mkdirSync,
  readFileSync,
  renameSync,
  rmSync,
  statSync,
  writeFileSync,
} from "node:fs";
import { homedir } from "node:os";
import { basename, dirname, isAbsolute, join, resolve } from "node:path";
import { fileURLToPath } from "node:url";

import {
  applyEdits,
  modify,
  parse,
  type ParseError,
} from "jsonc-parser";

import { resolveOpenCodePackageCache } from "./opencode-package-cache.js";

export const ORGX_OPENCODE_PLUGIN_PACKAGE = "@useorgx/orgx-opencode-plugin";
export const ORGX_OPENCODE_PLUGIN_SPEC = `${ORGX_OPENCODE_PLUGIN_PACKAGE}@alpha`;

export interface OpenCodePluginPaths {
  configPaths: readonly string[];
  packagePaths: readonly string[];
  packageSpecCacheRoot?: string;
}

export type OpenCodePluginEntryKind =
  | "managed-package"
  | "pinned-package"
  | "local-path";

export interface OpenCodePluginInspection {
  configPath: string;
  configExists: boolean;
  configured: boolean;
  entry: string | null;
  entryKind: OpenCodePluginEntryKind | null;
  packagePath: string | null;
  problem: string | null;
  runtimeWarning: string | null;
}

export interface OpenCodePluginConfigMutation {
  changed: boolean;
  message: string;
}

export interface OpenCodePluginConfigStatus {
  available: boolean;
  installed: boolean;
  message: string;
}

interface LoadedOpenCodeConfig {
  configPath: string;
  configExists: boolean;
  entries: string[];
  problem: string | null;
  text: string;
}

interface ClassifiedEntry {
  entry: string;
  entryKind: OpenCodePluginEntryKind;
  packagePath: string | null;
  problem: string | null;
}

function uniquePaths(paths: readonly string[]): string[] {
  return [...new Set(paths.filter((candidate) => candidate.trim().length > 0))];
}

export function openCodePluginPathsForHome(home = homedir()): OpenCodePluginPaths {
  const defaultHome = homedir();
  const configHome = home === defaultHome
    ? process.env.XDG_CONFIG_HOME?.trim() || join(home, ".config")
    : join(home, ".config");
  const cacheHome = home === defaultHome
    ? process.env.XDG_CACHE_HOME?.trim() || join(home, ".cache")
    : join(home, ".cache");
  const configDir = join(configHome, "opencode");

  return {
    configPaths: [
      join(configDir, "opencode.json"),
      join(configDir, "opencode.jsonc"),
    ],
    packagePaths: [
      join(
        cacheHome,
        "opencode",
        "node_modules",
        "@useorgx",
        "orgx-opencode-plugin",
        "package.json",
      ),
      join(configDir, "plugins", "orgx-opencode-plugin", "package.json"),
    ],
    packageSpecCacheRoot: join(cacheHome, "opencode", "packages"),
  };
}

function selectConfigPath(configPaths: readonly string[]): {
  configPath: string;
  problem: string | null;
} {
  const candidates = uniquePaths(configPaths);
  if (candidates.length === 0) {
    return {
      configPath: "",
      problem: "No OpenCode config path is available.",
    };
  }

  const existing = candidates.filter((candidate) => existsSync(candidate));
  if (existing.length > 1) {
    return {
      configPath: existing[0]!,
      problem: `Multiple OpenCode config files are present (${existing.join(", ")}); remove the ambiguity before changing plugins.`,
    };
  }

  return {
    configPath: existing[0] ?? candidates[0]!,
    problem: null,
  };
}

function loadOpenCodeConfig(configPaths: readonly string[]): LoadedOpenCodeConfig {
  const selection = selectConfigPath(configPaths);
  if (selection.problem) {
    return {
      configPath: selection.configPath,
      configExists: existsSync(selection.configPath),
      entries: [],
      problem: selection.problem,
      text: "",
    };
  }

  const configPath = selection.configPath;
  if (!existsSync(configPath)) {
    return {
      configPath,
      configExists: false,
      entries: [],
      problem: null,
      text: "{\n}\n",
    };
  }

  const file = lstatSync(configPath);
  if (file.isSymbolicLink() || !file.isFile()) {
    return {
      configPath,
      configExists: true,
      entries: [],
      problem: `OpenCode config ${configPath} is not a regular file; it was not changed.`,
      text: "",
    };
  }

  const text = readFileSync(configPath, "utf8");
  const errors: ParseError[] = [];
  const parsed: unknown = parse(text, errors, {
    allowEmptyContent: false,
    allowTrailingComma: true,
    disallowComments: false,
  });
  if (
    errors.length > 0 ||
    !parsed ||
    typeof parsed !== "object" ||
    Array.isArray(parsed)
  ) {
    return {
      configPath,
      configExists: true,
      entries: [],
      problem: `OpenCode config ${configPath} is not valid JSON/JSONC; it was not changed.`,
      text,
    };
  }

  const plugin = (parsed as Record<string, unknown>).plugin;
  if (plugin !== undefined && !Array.isArray(plugin)) {
    return {
      configPath,
      configExists: true,
      entries: [],
      problem: `OpenCode config ${configPath} has a non-array plugin field; it was not changed.`,
      text,
    };
  }
  if (Array.isArray(plugin) && plugin.some((entry) => typeof entry !== "string")) {
    return {
      configPath,
      configExists: true,
      entries: [],
      problem: `OpenCode config ${configPath} has a non-string plugin entry; it was not changed.`,
      text,
    };
  }

  return {
    configPath,
    configExists: true,
    entries: (plugin ?? []) as string[],
    problem: null,
    text,
  };
}

function packageSpecifierKind(entry: string): OpenCodePluginEntryKind | null {
  if (entry === ORGX_OPENCODE_PLUGIN_PACKAGE || entry === ORGX_OPENCODE_PLUGIN_SPEC) {
    return "managed-package";
  }
  if (entry.startsWith(`${ORGX_OPENCODE_PLUGIN_PACKAGE}@`)) {
    return "pinned-package";
  }
  return null;
}

function readPackageName(packagePath: string): string | null {
  if (!existsSync(packagePath)) return null;
  try {
    const parsed: unknown = JSON.parse(readFileSync(packagePath, "utf8"));
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const name = (parsed as Record<string, unknown>).name;
    return typeof name === "string" ? name : null;
  } catch {
    return null;
  }
}

function nearestPackagePath(localPath: string): string {
  if (basename(localPath) === "package.json") return localPath;
  if (!existsSync(localPath) || statSync(localPath).isDirectory()) {
    return join(localPath, "package.json");
  }

  let current = dirname(localPath);
  for (let depth = 0; depth < 6; depth += 1) {
    const candidate = join(current, "package.json");
    if (existsSync(candidate)) return candidate;
    const parent = dirname(current);
    if (parent === current) break;
    current = parent;
  }
  return join(dirname(localPath), "package.json");
}

function classifyLocalEntry(entry: string): ClassifiedEntry | null {
  let localPath: string;
  try {
    localPath = entry.startsWith("file://") ? fileURLToPath(entry) : entry;
  } catch {
    return entry.includes("orgx-opencode-plugin")
      ? {
          entry,
          entryKind: "local-path",
          packagePath: null,
          problem: `OpenCode config contains an invalid OrgX file URL: ${entry}`,
        }
      : null;
  }
  if (!isAbsolute(localPath)) return null;

  const packagePath = nearestPackagePath(resolve(localPath));
  const packageName = readPackageName(packagePath);
  if (packageName === ORGX_OPENCODE_PLUGIN_PACKAGE) {
    return {
      entry,
      entryKind: "local-path",
      packagePath,
      problem: null,
    };
  }
  if (!entry.includes("orgx-opencode-plugin")) return null;

  return {
    entry,
    entryKind: "local-path",
    packagePath: null,
    problem: existsSync(packagePath)
      ? `The OrgX plugin configured by OpenCode has unexpected package metadata at ${packagePath}.`
      : `The OrgX plugin configured by OpenCode is missing package metadata at ${packagePath}.`,
  };
}

function classifyOrgxEntry(entry: string): ClassifiedEntry | null {
  const entryKind = packageSpecifierKind(entry);
  if (entryKind) {
    return {
      entry,
      entryKind,
      packagePath: null,
      problem: null,
    };
  }
  return classifyLocalEntry(entry);
}

export function inspectOpenCodePlugin(
  paths: OpenCodePluginPaths,
): OpenCodePluginInspection {
  const loaded = loadOpenCodeConfig(paths.configPaths);
  if (loaded.problem) {
    return {
      configPath: loaded.configPath,
      configExists: loaded.configExists,
      configured: false,
      entry: null,
      entryKind: null,
      packagePath: null,
      problem: loaded.problem,
      runtimeWarning: null,
    };
  }

  const matches = loaded.entries
    .map(classifyOrgxEntry)
    .filter((entry): entry is ClassifiedEntry => entry !== null);
  if (matches.length > 1) {
    return {
      configPath: loaded.configPath,
      configExists: loaded.configExists,
      configured: false,
      entry: null,
      entryKind: null,
      packagePath: null,
      problem: `OpenCode config ${loaded.configPath} contains multiple OrgX plugin entries; remove the ambiguity before changing plugins.`,
      runtimeWarning: null,
    };
  }

  const match = matches[0];
  if (match?.problem) {
    return {
      configPath: loaded.configPath,
      configExists: loaded.configExists,
      configured: false,
      entry: match.entry,
      entryKind: match.entryKind,
      packagePath: null,
      problem: match.problem,
      runtimeWarning: null,
    };
  }

  if (match) {
    const cache = match.packagePath
      ? { packagePath: match.packagePath, problem: null }
      : resolveOpenCodePackageCache({
          packageName: ORGX_OPENCODE_PLUGIN_PACKAGE,
          packageEntry: match.entry,
          ...(paths.packageSpecCacheRoot
            ? { packageSpecCacheRoot: paths.packageSpecCacheRoot }
            : {}),
          legacyPackagePaths: paths.packagePaths,
        });
    if (cache.problem) {
      return {
        configPath: loaded.configPath,
        configExists: loaded.configExists,
        configured: false,
        entry: match.entry,
        entryKind: match.entryKind,
        packagePath: null,
        problem: cache.problem,
        runtimeWarning: null,
      };
    }
    return {
      configPath: loaded.configPath,
      configExists: loaded.configExists,
      configured: true,
      entry: match.entry,
      entryKind: match.entryKind,
      packagePath: cache.packagePath,
      problem: null,
      runtimeWarning: match.entryKind !== "local-path" && !cache.packagePath
        ? "The OrgX OpenCode package is configured but is not in OpenCode's cache yet; start OpenCode once, then re-run hooks doctor --json."
        : null,
    };
  }

  const cache = resolveOpenCodePackageCache({
    packageName: ORGX_OPENCODE_PLUGIN_PACKAGE,
    packageEntry: null,
    ...(paths.packageSpecCacheRoot
      ? { packageSpecCacheRoot: paths.packageSpecCacheRoot }
      : {}),
    legacyPackagePaths: paths.packagePaths,
  });
  return {
    configPath: loaded.configPath,
    configExists: loaded.configExists,
    configured: false,
    entry: null,
    entryKind: null,
    packagePath: cache.packagePath,
    problem: cache.problem,
    runtimeWarning: null,
  };
}

export function summarizeOpenCodePluginStatus(
  paths: OpenCodePluginPaths,
  hostAvailable: boolean,
): OpenCodePluginConfigStatus {
  const inspection = inspectOpenCodePlugin(paths);
  const available = hostAvailable || inspection.configExists || Boolean(inspection.packagePath);

  if (inspection.problem) {
    return {
      available,
      installed: false,
      message: inspection.problem,
    };
  }
  if (inspection.configured) {
    return {
      available: true,
      installed: true,
      message: inspection.runtimeWarning ?? (
        inspection.entryKind === "local-path"
          ? "OpenCode is configured to load a verified local OrgX plugin package."
          : "OpenCode is configured to load the OrgX plugin package."
      ),
    };
  }
  if (inspection.packagePath) {
    return {
      available: true,
      installed: true,
      message: "A legacy local OrgX OpenCode plugin package is installed and was preserved.",
    };
  }
  return {
    available,
    installed: false,
    message: available
      ? "OpenCode is available and ready for OrgX plugin configuration."
      : "OpenCode was not detected.",
  };
}

function writeConfigAtomically(
  configPath: string,
  previousText: string,
  entries: readonly string[] | undefined,
): void {
  const edits = modify(previousText, ["plugin"], entries, {
    formattingOptions: {
      eol: previousText.includes("\r\n") ? "\r\n" : "\n",
      insertSpaces: true,
      tabSize: 2,
    },
    isArrayInsertion: false,
  });
  const nextText = applyEdits(previousText, edits);
  const parent = dirname(configPath);
  mkdirSync(parent, { recursive: true });
  const temporaryPath = join(parent, `.opencode.json.orgx-wizard-${process.pid}.tmp`);
  const mode = existsSync(configPath) ? lstatSync(configPath).mode & 0o777 : 0o600;

  try {
    writeFileSync(temporaryPath, nextText, { encoding: "utf8", mode });
    renameSync(temporaryPath, configPath);
  } finally {
    rmSync(temporaryPath, { force: true });
  }
}

function mutateOpenCodePluginConfig(
  paths: OpenCodePluginPaths,
  operation: "install" | "uninstall",
): OpenCodePluginConfigMutation {
  const loaded = loadOpenCodeConfig(paths.configPaths);
  const inspection = inspectOpenCodePlugin(paths);
  if (loaded.problem || inspection.problem) {
    throw new Error(loaded.problem ?? inspection.problem ?? "OpenCode plugin configuration is invalid.");
  }

  if (operation === "install") {
    if (inspection.entryKind === "local-path") {
      return {
        changed: false,
        message: "A valid local OrgX OpenCode plugin entry is already configured and was preserved.",
      };
    }
    if (inspection.entryKind === "pinned-package") {
      return {
        changed: false,
        message: "A pinned OrgX OpenCode plugin package is already configured and was preserved.",
      };
    }
    if (inspection.entry === ORGX_OPENCODE_PLUGIN_SPEC) {
      return {
        changed: false,
        message: `${ORGX_OPENCODE_PLUGIN_SPEC} is already configured.`,
      };
    }
    if (!inspection.entry && inspection.packagePath) {
      return {
        changed: false,
        message: "An existing local OrgX OpenCode plugin installation was preserved without adding a duplicate package entry.",
      };
    }

    const nextEntries = inspection.entry === ORGX_OPENCODE_PLUGIN_PACKAGE
      ? loaded.entries.map((entry) => entry === ORGX_OPENCODE_PLUGIN_PACKAGE
        ? ORGX_OPENCODE_PLUGIN_SPEC
        : entry)
      : [...loaded.entries, ORGX_OPENCODE_PLUGIN_SPEC];
    writeConfigAtomically(loaded.configPath, loaded.text, nextEntries);
    return {
      changed: true,
      message: inspection.entry === ORGX_OPENCODE_PLUGIN_PACKAGE
        ? `Updated the managed OpenCode plugin entry to ${ORGX_OPENCODE_PLUGIN_SPEC}.`
        : `Added ${ORGX_OPENCODE_PLUGIN_SPEC} to ${loaded.configPath}; OpenCode installs npm plugins on launch.`,
    };
  }

  if (
    inspection.entry !== ORGX_OPENCODE_PLUGIN_SPEC &&
    inspection.entry !== ORGX_OPENCODE_PLUGIN_PACKAGE
  ) {
    const preserved = inspection.entryKind === "local-path" || inspection.entryKind === "pinned-package";
    return {
      changed: false,
      message: preserved
        ? "The OpenCode OrgX entry is user-managed and was preserved."
        : "The managed OpenCode OrgX plugin entry was not installed.",
    };
  }

  const nextEntries = loaded.entries.filter((entry) => entry !== inspection.entry);
  writeConfigAtomically(
    loaded.configPath,
    loaded.text,
    nextEntries.length > 0 ? nextEntries : undefined,
  );
  return {
    changed: true,
    message: "Removed the managed OrgX OpenCode package entry and preserved all unrelated config.",
  };
}

export function installOpenCodePluginConfig(
  paths: OpenCodePluginPaths,
  options: { hostAvailable?: boolean } = {},
): OpenCodePluginConfigMutation {
  const inspection = inspectOpenCodePlugin(paths);
  const available = options.hostAvailable !== false ||
    inspection.configExists ||
    Boolean(inspection.packagePath);
  if (!available) {
    return {
      changed: false,
      message: "OpenCode is not available on this machine.",
    };
  }
  return mutateOpenCodePluginConfig(paths, "install");
}

export function uninstallOpenCodePluginConfig(
  paths: OpenCodePluginPaths,
): OpenCodePluginConfigMutation {
  return mutateOpenCodePluginConfig(paths, "uninstall");
}
